import { useContext, useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { nanoid } from 'nanoid'
import moment from 'moment'

import { CardCompany } from './ui'
import { CardListenerTabFiles } from './ui.tab.files'
import { CardListenerTabPrimary } from './ui.tab.primary'
import { FieldsErrorType, ICardCompany, ICardCompanyContainer, ICardCompanyTabFiles, ICardCompanyTabPrimary, IFieldsError, IFilesList } from './types'

import { useCompanies } from '@api/companies/state'
import { ICompanyCard } from '@api/companies/state.types'

import CommonService from '@services/common.service'
import { reportUnexpectedClientError } from '@services/client-error.service'

import { useValue } from '@hooks/useValue'
import { useCardSaveGuard } from '@hooks/useCardSaveGuard'
import { useFieldsErrorAnimation } from '@hooks/useFieldsErrorAnimation'
import { useDebounce } from '@hooks/useDebounce'
import { isValidINN, validFormData } from '@hooks/useForm'

import { IModalNotice, ModalNotice } from '@primitive/modal.notice'
import { IModalPreview, ModalPreview } from '@primitive/modal.preview'
import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'
import { useCommon } from '@api/common/state'
import { CardModeType } from '@smart/common.type'


const DEFAULT_COMPANY_REQUISITES = 'Банк: \nР/сч: \nК/сч: \nБИК: '

type CompanyFormState = {
    companyINN: string
    companyKPP: string
    isForeignCompany: boolean
    companyPhone: string
    companyEmail: string
    companyName: string
    companyNameShort: string
    managerPosition: string
    actingOnBasis: string
    lastName: string
    firstName: string
    secondName: string
    inPersonWhom: string
    address: string
    requisites: string
    comment: string
    pendingFiles: string[]
}

const DEFAULT_COMPANY_FORM_STATE: CompanyFormState = {
    companyINN: '',
    companyKPP: '',
    isForeignCompany: false,
    companyPhone: '',
    companyEmail: '',
    companyName: '',
    companyNameShort: '',
    managerPosition: '',
    actingOnBasis: '',
    lastName: '',
    firstName: '',
    secondName: '',
    inPersonWhom: '',
    address: '',
    requisites: DEFAULT_COMPANY_REQUISITES,
    comment: '',
    pendingFiles: [],
}

const createCompanyFormSnapshot = (state: CompanyFormState) => JSON.stringify(state)
const createCompanyDeclinationSignature = (lastName: string, firstName: string, secondName: string, managerPosition: string) => JSON.stringify([lastName, firstName, secondName, managerPosition])

const getCompanyFormState = (company: ICompanyCard): CompanyFormState => ({
    companyINN: company.companyINN || '',
    companyKPP: company.companyKPP || '',
    isForeignCompany: company.isForeignCompany === 1,
    companyPhone: company.companyPhone || '',
    companyEmail: company.companyEmail || '',
    companyName: company.companyName || '',
    companyNameShort: company.companyNameShort || '',
    managerPosition: company.managerPosition || '',
    actingOnBasis: company.actingOnBasis || '',
    lastName: company.lastName || '',
    firstName: company.firstName || '',
    secondName: company.secondName || '',
    inPersonWhom: company.inPersonWhom || '',
    address: company.address || '',
    requisites: company.requisites || DEFAULT_COMPANY_REQUISITES,
    comment: company.comment || '',
    pendingFiles: [],
})


/** Карточка по контрагенту - Контейнер */
export const CardCompanyContainer = ({isOpen, cardMode, cardID, handlerButtonOk, handlerButtonCancel}: ICardCompanyContainer) => {
    const notify = useContext(NotificationContext)

    const filesListSendToSrv = useValue<File[]>([]) // файлы для отпарвки на сервер, при создании
    const filesList = useValue<IFilesList[]>([]) // просто список файлов

    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    const modalNotice = useValue<IModalNotice>({isOpen: false})
    const modalPreview = useValue<IModalPreview>({isOpen: false})

    const tabActive = useValue<string>('primary')
    const fieldsError = useValue<IFieldsError>({})
    const currentCardID = useValue<number>(0)
    const currentCardMode = useValue<CardModeType>('new')
    const isDisableBtnSave = useValue<boolean>(true)
    const savedFormSnapshotRef = useRef(createCompanyFormSnapshot(DEFAULT_COMPANY_FORM_STATE))
    const savedDeclinationSignatureRef = useRef(createCompanyDeclinationSignature('', '', '', ''))
    const hasSavedChangesRef = useRef(false)
    const saveGuard = useCardSaveGuard(isOpen, `${cardMode}:${cardID}`, () => {
        hasSavedChangesRef.current = true
        notify && notifyOpen('Результат сохранения не подтверждён. Анкета могла быть создана. Повторное создание заблокировано; закройте анкету и проверьте список.', 'warning', 10000, notify)
    })
    const isFormReadyRef = useRef(false)

    const companyNumber = useValue<number | ''>('')
    const companyDateCreate = useValue<string>('')
    const commpanyINNAuto = useValue<boolean>(false)
    const isReadOnlyKPP = useValue<boolean>(true)

    const txtINNBuff = useValue<string>('') // Буффер, оригинальный ИНН
    const txtKPPBuff = useValue<string>('') // Буффер, оригинальный КПП
    /** Поле ИНН контрагента */
    const txtINN = useValue<string>('')
    /** Поле КПП контрагента */
    const txtKPP = useValue<string>('')
    const txtCompanyPhone = useValue<string>('')
    const txtCompanyEmail = useValue<string>('')
    /** Полное наименование контрагента */
    const txtCompanyName = useValue<string>('')
    /** Краткое наименование контрагента */
    const txtCompanyNameShort = useValue<string>('')

    const txtManagerPositionBuff = useValue<string>('') // Буффер, оригинальная должность
    const txtActingOnBasisBuff = useValue<string>('') // Буффер, оригинальная действующего на основании
    /** Действующего на основании */
    const txtActingOnBasis = useValue<string>('')

    /** Поле в лице кого */
    const txtInPersonWhom = useValue<string>('')

    /** Адрес контрагента */
    const txtAddress = useValue<string>('')
    const txtRequisites = useValue<string>(DEFAULT_COMPANY_REQUISITES)
    
    const txtComment = useValue<string>('')

    const {
        isLoadingCard, getCardData, setCardData, checkINN, 
        checkKPP, deleteFiles, uploadFiles, getFiles, searchCompany
    } = useCompanies(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        searchCompany: state.actionGetSearchCompany,
        getCardData: state.actionGetCompanyCard,
        setCardData: state.actionSetCompanyCard,
        checkINN: state.actionGetCompanyCheckINN,
        checkKPP: state.actionGetCompanyCheckKPP,
        deleteFiles: state.actionSetCompanyDeleteFiles,
        uploadFiles: state.actionSetCompanyUploadFiles,
        getFiles: state.actionGetCompanyFiles,
    })))

    const { getDeclinationString } = useCommon(useShallow((state) => ({
        getDeclinationString: state.actionGetDeclinationString,
    })))

    //===========================================================================================

    /** Должность контрагента */
    const [txtManagerPosition, setTxtManagerPosition] = useState<string>('')
    /** Фамилия контрагента */
    const [txtLastName, setTxtLastName] = useState<string>('')
    /** Имя контрагента */
    const [txtFirstName, setTxtFirstName] = useState<string>('')
    /** Отчество контрагента */
    const [txtSecondName, setTxtSecondName] = useState<string>('')

    const getCurrentFormSnapshot = (pendingFiles: File[] = filesListSendToSrv.value) => createCompanyFormSnapshot({
        companyINN: txtINN.value,
        companyKPP: txtKPP.value,
        isForeignCompany: commpanyINNAuto.value,
        companyPhone: txtCompanyPhone.value,
        companyEmail: txtCompanyEmail.value,
        companyName: txtCompanyName.value,
        companyNameShort: txtCompanyNameShort.value,
        managerPosition: txtManagerPosition,
        actingOnBasis: txtActingOnBasis.value,
        lastName: txtLastName,
        firstName: txtFirstName,
        secondName: txtSecondName,
        inPersonWhom: txtInPersonWhom.value,
        address: txtAddress.value,
        requisites: txtRequisites.value,
        comment: txtComment.value,
        pendingFiles: pendingFiles.map((file) => `${file.name}:${file.size}:${file.lastModified}`),
    })

    const applyCompanyCardData = (company: ICompanyCard) => {
        const formState = getCompanyFormState(company)

        currentCardID.setValue(company.companyID)
        currentCardMode.setValue('edit')
        companyNumber.setValue(company.companyID)
        companyDateCreate.setValue(moment(company.dateCreate).format('DD.MM.YYYY'))

        commpanyINNAuto.setValue(formState.isForeignCompany)
        isReadOnlyKPP.setValue(formState.companyINN.length >= 12)
        txtINNBuff.setValue(formState.companyINN)
        txtINN.setValue(formState.companyINN)
        txtKPPBuff.setValue(formState.companyKPP)
        txtKPP.setValue(formState.companyKPP)
        txtCompanyPhone.setValue(formState.companyPhone)
        txtCompanyEmail.setValue(formState.companyEmail)
        txtCompanyName.setValue(formState.companyName)
        txtCompanyNameShort.setValue(formState.companyNameShort)
        setTxtLastName(formState.lastName)
        setTxtFirstName(formState.firstName)
        setTxtSecondName(formState.secondName)
        txtManagerPositionBuff.setValue(formState.managerPosition)
        setTxtManagerPosition(formState.managerPosition)
        txtActingOnBasisBuff.setValue(formState.actingOnBasis)
        txtActingOnBasis.setValue(formState.actingOnBasis)
        txtInPersonWhom.setValue(formState.inPersonWhom)
        txtAddress.setValue(formState.address)
        txtRequisites.setValue(formState.requisites)
        txtComment.setValue(formState.comment)
        filesListSendToSrv.setValue([])
        filesList.setValue(company.filesList.map((item) => ({
            fileID: item.companyFileID,
            fileName: item.fileName,
            filePath: item.filePath,
            fileSize: item.fileSize,
            dateCreate: item.dateCreate,
            isSelected: false
        })))

        savedFormSnapshotRef.current = createCompanyFormSnapshot(formState)
        savedDeclinationSignatureRef.current = createCompanyDeclinationSignature(formState.lastName, formState.firstName, formState.secondName, formState.managerPosition)
        isFormReadyRef.current = true
        isDisableBtnSave.setValue(true)
    }

    useEffect(() => {
        if (!isOpen) return

        const declinationSignature = createCompanyDeclinationSignature(txtLastName, txtFirstName, txtSecondName, txtManagerPosition)
        if (declinationSignature === savedDeclinationSignatureRef.current) return

        const lastName = txtLastName?.trim().charAt(0).toUpperCase() + txtLastName?.trim().toLowerCase().slice(1)
        const firstName = txtFirstName?.trim().charAt(0).toUpperCase() + txtFirstName?.trim().toLowerCase().slice(1)
        const secondName = txtSecondName?.trim().charAt(0).toUpperCase() + txtSecondName?.trim().toLowerCase().slice(1)
        const fullName = `${lastName} ${firstName} ${secondName}`.trim()
        if (!fullName) return

        const managerPosition = `${txtManagerPosition?.trim().charAt(0).toLowerCase() + txtManagerPosition?.trim().toLowerCase().slice(1)}`
        let isCurrentRequest = true
        const timeoutID = setTimeout(() => {
            getDeclinationString(fullName, managerPosition).then((res) => {
                if (isCurrentRequest && res.data?.result) txtInPersonWhom.setValue(res.data.result)
            }).catch((res) => {
                reportUnexpectedClientError(res, 'CardCompany.getDeclinationString')
                if (isCurrentRequest) notify && notifyOpen(res.message, 'error', 1500, notify)
            })
        }, 2000)

        return () => {
            isCurrentRequest = false
            clearTimeout(timeoutID)
        }
    }, [isOpen, txtLastName, txtFirstName, txtSecondName, txtManagerPosition]) // eslint-disable-line react-hooks/exhaustive-deps

    //===========================================================================================

    useEffect(() => {
        let isCurrentRequest = true

        if (isOpen) {
            clearCard()
            currentCardID.setValue(Number(cardID) || 0)
            currentCardMode.setValue(cardMode || 'new')

            if (cardMode === 'new') {
                isFormReadyRef.current = true
            }

            cardMode === 'edit' && getCardData(Number(cardID)).then((res) => {
                if (!isCurrentRequest) return
                if (res) applyCompanyCardData(res)
            })
        }

        !isOpen && clearCard() // Обнуление данных по анкете

        return () => {
            isCurrentRequest = false
        }
    }, [isOpen, cardID, cardMode]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = getCurrentFormSnapshot()
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    useEffect(() => {
        if (isReadOnlyKPP.value && !commpanyINNAuto.value) {
            setTxtManagerPosition('')
            txtActingOnBasis.setValue('')
            txtKPP.setValue('')
        } else {
            setTxtManagerPosition(txtManagerPositionBuff.value || '')
            txtActingOnBasis.setValue(txtActingOnBasisBuff.value || 'Устава')
            !commpanyINNAuto.value && txtKPP.setValue(txtKPPBuff.value || '')
        }
    }, [isReadOnlyKPP.value, commpanyINNAuto.value]) // eslint-disable-line react-hooks/exhaustive-deps
    
    //===========================================================================================

    /** Функция обнуления данных по карточке */
    const clearCard = () => {
        tabActive.setValue('primary')
        fieldsError.setValue({})
        currentCardID.setValue(0)
        currentCardMode.setValue('new')
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        savedFormSnapshotRef.current = createCompanyFormSnapshot(DEFAULT_COMPANY_FORM_STATE)
        savedDeclinationSignatureRef.current = createCompanyDeclinationSignature('', '', '', '')

        companyNumber.setValue('')
        companyDateCreate.setValue('')
        commpanyINNAuto.setValue(false)
        isReadOnlyKPP.setValue(true)

        txtINNBuff.setValue('') // Буффер, оригинальный ИНН
        txtINN.setValue('')
        txtKPPBuff.setValue('') // Буффер, оригинальный КПП
        txtKPP.setValue('')
        txtCompanyPhone.setValue('')
        txtCompanyEmail.setValue('')
        txtCompanyName.setValue('')

        txtCompanyNameShort.setValue('')
        setTxtLastName('')
        setTxtFirstName('')
        setTxtSecondName('')
        txtManagerPositionBuff.setValue('') // Буффер, оригинальная должность
        setTxtManagerPosition('')
        txtActingOnBasisBuff.setValue('') // Буффер, оригинальная действующего на основании
        txtActingOnBasis.setValue('')

        txtInPersonWhom.setValue('')
        txtAddress.setValue('')
        txtRequisites.setValue(DEFAULT_COMPANY_REQUISITES)

        txtComment.setValue('')

        filesList.setValue([])
        filesListSendToSrv.setValue([])
    }

    /** Функция обнуления данных по карточке */
    const getCardPage = (tabActive: string) => {
        switch (tabActive) {
            case 'primary': return <CardListenerTabPrimary {...propsToTabPrimary} />
            case 'files': return <CardListenerTabFiles {...propsToTabFiles} />
        }
    }

    // Загрузка файлов на сервер
    const fileUploader = async (files: File[]) => {
        const maxSize = 10485760 // Максимально разрешенный размер файла для загрузки (10 MB)

        switch (currentCardMode.value) {
            case 'new': { // При создании контрагента
                const newFiles: IFilesList[] = files.reduce<IFilesList[]>((acc, item) => {
                    // Проверка на максимальный размер файла
                    if (item.size > maxSize) {
                        notify && notifyOpen(`Файл ${item.name}, превышает максимально допустимый размер в 10 мегабайт.`, 'error', 1500, notify)
                        return acc
                    }

                    // Проверка на уникальность имени файла
                    const isAlreadyAdded = filesList.value.some(existingFile => existingFile.fileName === item.name)
                    if (isAlreadyAdded) {
                        notify && notifyOpen(`Файл с именем ${item.name} уже добавлен в список.`, 'error', 1500, notify)
                        return acc
                    }

                    // Добавление нового файла
                    const newFile: IFilesList = {
                        fileID: Number(CommonService.generateDigits(10)),
                        fileName: item.name,
                        filePath: '',
                        fileSize: CommonService.getDefiningFileUnit(item.size),
                        dateCreate: new Date().toISOString(),
                        isSelected: false
                    }

                    acc.push(newFile)
                    return acc
                }, [])

                // Обновление списка файлов, добавляя новые файлы
                filesList.setValue([...filesList.value, ...newFiles])

                const newFilesOnly = newFiles.map(f => files.find(file => file.name === f.fileName)).filter(f => f) as File[]
                filesListSendToSrv.setValue([...filesListSendToSrv.value, ...newFilesOnly])
                break
            }

            case 'edit': { // При редактировании контрагента
                files.forEach((file) => { // Проверка файлов

                })

                await uploadFiles(currentCardID.value, files).finally(() => {
                    getFiles(currentCardID.value).then((files) => {
                        filesList.setValue(files.map((item) => ({
                            fileID: item.companyFileID,
                            fileName: item.fileName,
                            filePath: item.filePath,
                            fileSize: item.fileSize,
                            dateCreate: item.dateCreate,
                            isSelected: false
                        })))
                    })
                })
                break
            }
        }
    }


    /** Проверка по ИНН, существование контрагента */
    const companyCheckINN = async (inn: string) => {
        const res = await checkINN(currentCardID.value, inn)
        if (res?.result) handleOpenModalNotice(true, 'Извещение...', `<b>Внимание!</b><br/>Контрагент с таким ИНН уже зарегистрирован, идентификатор анкеты контрагента: <b>${res.appID}</b>`)

        searchCompany(inn, txtKPP.value).then((res) => {
            switch (res?.type) {
                case 'LEGAL': { // Если организация
                    !txtKPP.value && txtKPP.setValue(res.kpp || ''); txtKPPBuff.setValue(res.kpp || '')
                    !txtCompanyName.value && txtCompanyName.setValue(res.companyFullName || '')
                    !txtCompanyNameShort.value && txtCompanyNameShort.setValue(res.companyShortName || '')
                    !txtManagerPosition && setTxtManagerPosition(res.directorPost || ''); txtManagerPositionBuff.setValue(res.directorPost || '')
                    !txtLastName && setTxtLastName(res.directorLastName)
                    !txtFirstName && setTxtFirstName(res.directorFirstName)
                    !txtSecondName && setTxtSecondName(res.directorSecondName)
                    !txtInPersonWhom.value && txtInPersonWhom.setValue(res.representedBy)
                    !txtAddress.value && txtAddress.setValue(res.address)
                    break
                }

                case 'INDIVIDUAL': { // Если ИП
                    !txtKPP.value && txtKPP.setValue(res.kpp || ''); txtKPPBuff.setValue(res.kpp || '')
                    !txtCompanyName.value && txtCompanyName.setValue(res.companyFullName || '')
                    !txtCompanyNameShort.value && txtCompanyNameShort.setValue(res.companyShortName || '')
                    !txtLastName && setTxtLastName(res.directorLastName)
                    !txtFirstName && setTxtFirstName(res.directorFirstName)
                    !txtSecondName && setTxtSecondName(res.directorSecondName)
                    !txtInPersonWhom.value && txtInPersonWhom.setValue(res.representedBy)
                    !txtAddress.value && txtAddress.setValue(res.address)
                    break
                }
            }
        })

        // 2720063738
        // 272001001

        // ОБЩЕСТВО С ОГРАНИЧЕННОЙ ОТВЕТСТВЕННОСТЬЮ "ИМПЕРИЯ ФИНАНСОВ"
        // ООО "ИМПЕРИЯ ФИНАНСОВ"

        // Генеральный директор
        // Устава
        // Горбунова Ольга Алексеевна

        // 680510, ХАБАРОВСКИЙ КРАЙ, М.Р-Н ХАБАРОВСКИЙ, С.П. ТОПОЛЕВСКОЕ, С. ТОПОЛЕВО, УЛ. ГАРАЖНАЯ, Д. 52, КВ. 26

        // Счёт:  40702810509280003353
        // Банк:  ФИЛИАЛ "ЦЕНТРАЛЬНЫЙ" БАНКА ВТБ (ПАО)
        // Корр. счёт:  30101810145250000411
        // БИК:  044525411
        // Email: IMPERIYA.FINANSOV@MAIL.RU        
    }
    /** Проверка по КПП, существование контрагента */
    const companyCheckKPP = async (kpp: string) => {
        const res = await checkKPP(currentCardID.value, kpp)
        if (res?.result) handleOpenModalNotice(true, 'Извещение...', `<b>Внимание!</b><br/>Контрагент с таким КПП уже зарегистрирован, идентификатор анкеты контрагента: <b>${res.appID}</b>`)

        // searchCompany(txtINN.value, kpp)
    }


    /** Активация анимации ошибки в указанных полях */
    const fieldsErrorAnumation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)


    /** Проверка ИНН на дубль */
    const debounceINN = useDebounce(companyCheckINN, 1500)
    const handleChangeINN = (value: string) => {
        txtINN.setValue(value)
        debounceINN(value)

        if (!commpanyINNAuto.value && isValidINN(value)) {
            if (value.length === 10) {
                isReadOnlyKPP.setValue(false)
            } else {
                isReadOnlyKPP.setValue(true)
            }
        } else { isReadOnlyKPP.setValue(true) }
    }
    /** Проверка КПП на дубль */
    const debounceKPP = useDebounce(companyCheckKPP, 1500)
    const handleChangeKPP = (value: string) => {
        txtKPP.setValue(value)
        debounceKPP(value)
    }

    //===========================================================================================

    /** Функция по генерации ИНН */
    const handleAutoINN = (checked: boolean) => {
        commpanyINNAuto.setValue(checked)

        if (checked) {
            txtINN.setValue(nanoid())
            txtKPP.setValue('')
            isReadOnlyKPP.setValue(true)
        } else {
            txtINN.setValue(txtINNBuff.value || '')
            txtKPP.setValue(txtKPPBuff.value || '')

            if (txtINNBuff.value?.length === 10) {
                isReadOnlyKPP.setValue(false)
            }

            let innFromBuffer: string = ''
            if (txtINNBuff.value.length === 12) innFromBuffer = txtINNBuff.value

            txtINN.setValue(innFromBuffer || '')
        }
    }

    /** Открытие/закрытие окна извещения */
    const handleOpenModalNotice = (isOpen: boolean, headerText: string, contentText: string) => {
        modalNotice.setValue({
            isOpen, headerText, contentText, 
            handleButtonOk() {modalNotice.setValue({isOpen: false})}, 
        })
    }
    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handlerButtonOk && handlerButtonOk()
        } else {
            handlerButtonCancel && handlerButtonCancel()
        }
    }
    const handleBtnClose = () => {
        if (isLoadingCard) return

        if (isDisableBtnSave.value) {
            closeCard()
            return
        }

        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Сохранение формы',
            contentText: '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?',
            cancelButtonText: 'Продолжить редактирование',
            okButtonText: 'Закрыть без сохранения',
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            handleButtonOk() {closeCard()},
        })
    }

    /** Открытие/закрытие окна предпросмотра документа */
    const handleOpenModalPreview = (isOpen: boolean, fileUrl: string, fileName: string) => {
        modalPreview.setValue({
            isOpen, fileUrl, fileName, 
            handleButtonCancel() {modalPreview.setValue({isOpen: false})}, 
        })
    }

    /** Функция по выделению файлов */
    const handleSelectedFiles = (fileID: number) => {
        const newList = filesList.value.map(item => {
            if (item.fileID === fileID) {
                return { ...item, isSelected: !item.isSelected }
            }

            return item
        })

        filesList.setValue(newList)
    }

    /** Функция по выделению всех файлов */
    const handleSelectedFilesAll = () => {
        const newList = filesList.value.map(item => {
            return { ...item, isSelected: !item.isSelected }
        })

        filesList.setValue(newList)
    }

    /** Функция по скачиванию файлов контрагента */
    const handleDownloadFiles = (fileID?: number) => {
        let selectedFiles: IFilesList[] = []

        if (currentCardMode.value === 'new') return notify && notifyOpen(`Не возможно скачать файл или файлы, т.к. они еще не загружены на сервер.`, 'warning', 1500, notify)

        if (fileID) { selectedFiles = filesList.value.filter(file => file.fileID === fileID) } 
        else { selectedFiles = filesList.value.filter(file => file.isSelected) }

        for (const file of selectedFiles) {
            const link = document.createElement('a') // Создание ссылки и программное скачивание файла

            link.href = file.filePath
            link.download = file.fileName
            document.body.appendChild(link)

            link.click()
            document.body.removeChild(link)
        }
    }

    /** Функция по удалению файлов контрагента */
    const handleDeleteFiles = async (fileID?: number) => {
        let selectedFiles: IFilesList[] = []

        if (fileID) { selectedFiles = filesList.value.filter(file => file.fileID === fileID) } 
        else { selectedFiles = filesList.value.filter(file => file.isSelected) }

        switch (currentCardMode.value) {
            case 'new': { // При создании контрагента               
                const fileNamesToDelete = selectedFiles.map(file => file.fileName) // Получаем имена файлов, которые нужно удалить
                
                // Обновляем filesList, удаляя выбранные файлы
                filesList.setValue(filesList.value.filter(file => !fileNamesToDelete.includes(file.fileName)))
                
                // Обновляем filesListSendToSrv, удаляя файлы, чьи имена находятся в fileNamesToDelete
                filesListSendToSrv.setValue(filesListSendToSrv.value.filter(file => !fileNamesToDelete.includes(file.name)));
                break
            }
            case 'edit': { // При редактировании контрагента
                for (const file of selectedFiles) {
                    await deleteFiles(currentCardID.value, file.fileID).finally(() => {
                        getFiles(currentCardID.value).then((files) => {
                            filesList.setValue(files.map((item) => ({
                                fileID: item.companyFileID,
                                fileName: item.fileName,
                                filePath: item.filePath,
                                fileSize: item.fileSize,
                                dateCreate: item.dateCreate,
                                isSelected: false
                            })))
                        })
                    })
                } break
            }
        }
    }

    /** Кнопка сохранить все данные во вкладке primary */
    const handleBtnSave = (isClosedModal: boolean) => {
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'inn', value: txtINN.value, validType: commpanyINNAuto.value ? 'notEmpty' : 'inn', isRequired: true, errorMessage: 'ИНН контрагента не корректный.'},
            {fieldName: 'kpp', value: txtKPP.value, validType: 'kpp', isRequired: isReadOnlyKPP.value ? false : true, errorMessage: 'КПП контрагента не корректный.'},

            {fieldName: 'companyName', value: txtCompanyName.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Полное наименование контрагента, обязательно должно быть заполнено.'},
            {fieldName: 'companyNameShort', value: txtCompanyNameShort.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Краткое наименование контрагента, обязательно должно быть заполнено.'},
            
            {fieldName: 'managerPosition', value: txtManagerPosition, validType: 'notEmpty', isRequired: (isReadOnlyKPP.value && !commpanyINNAuto.value) ? false : true, errorMessage: 'Должность руководителя, обязательно должно быть заполнено.'},
            {fieldName: 'actingOnBasis', value: txtActingOnBasis.value, validType: 'notEmpty', isRequired: (isReadOnlyKPP.value && !commpanyINNAuto.value) ? false : true, errorMessage: 'Действующего на основании, обязательно должно быть заполнено.'},
            
            {fieldName: 'lastName', value: txtLastName, validType: 'notEmpty', isRequired: true, errorMessage: 'Фамилия руководителя, обязательно нужно заполнить.'},
            {fieldName: 'firstName', value: txtFirstName, validType: 'notEmpty', isRequired: true, errorMessage: 'Имя руководителя, обязательно нужно заполнить.'},
            
            {fieldName: 'inPersonWhom', value: txtInPersonWhom.value, validType: 'notEmpty', isRequired: true, errorMessage: 'В лице кого, обязательно нужно заполнить.'},
            {fieldName: 'address', value: txtAddress.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Юридический адрес, обязательно нужно заполнить.'},
            {fieldName: 'requisites', value: txtRequisites.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Платёжные реквизиты, обязательно нужно заполнить.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnumation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }

        if (validateData.length === 0) { // Сохраняем данные
            saveGuard.runSave(() => setCardData(currentCardID.value, {
                actionForm: currentCardMode.value,
            
                companyINN: txtINN.value,
                companyKPP: txtKPP.value,
                companyPhone: txtCompanyPhone.value,
                companyEmail: txtCompanyEmail.value,

                companyName: txtCompanyName.value,
                companyNameShort: txtCompanyNameShort.value,
            
                managerPosition: txtManagerPosition,
                lastName: txtLastName,
                firstName: txtFirstName,
                secondName: txtSecondName,
                actingOnBasis: txtActingOnBasis.value,
            
                inPersonWhom: txtInPersonWhom.value,
                address: txtAddress.value,
            
                requisites: txtRequisites.value,
            
                comment: txtComment.value,
                
                isForeignCompany: commpanyINNAuto.value ? 1 : 0
            }, filesListSendToSrv.value), {isNew: currentCardMode.value === 'new', getSavedID: response => response.companyID}).then((res) => {
                if (res?.status === 200) {
                    const savedCompanyID = Number(res.companyID || currentCardID.value)
                    if (!Number.isSafeInteger(savedCompanyID) || savedCompanyID <= 0) {
                        notify && notifyOpen('Сервер не вернул идентификатор сохранённого контрагента.', 'error', 2000, notify)
                        return
                    }

                    notify && notifyOpen(res.warning || 'Данные успешно сохранены.', res.warning ? 'warning' : 'success', res.warning ? 7000 : 1500, notify)
                    hasSavedChangesRef.current = true

                    if (isClosedModal && !res.warning) {
                        handlerButtonOk && handlerButtonOk()
                        return
                    }

                    currentCardID.setValue(savedCompanyID)
                    currentCardMode.setValue('edit')
                    companyNumber.setValue(savedCompanyID)
                    filesListSendToSrv.setValue([])
                    savedFormSnapshotRef.current = getCurrentFormSnapshot([])
                    savedDeclinationSignatureRef.current = createCompanyDeclinationSignature(txtLastName, txtFirstName, txtSecondName, txtManagerPosition)
                    isDisableBtnSave.setValue(true)

                    getCardData(savedCompanyID).then((company) => {
                        if (company) applyCompanyCardData(company)
                    })
                }
            })
        }
    }

    //===========================================================================================

    /** Свойства передаваемые в компоненту */
    const propsToUI: ICardCompany = {
        isOpen, cardMode: currentCardMode.value, filesList, 
        isLoading: isLoadingCard, tabActive,
        isDisableBtnSave: {...isDisableBtnSave, value: isDisableBtnSave.value || saveGuard.isBlocked},
        companyNumber, companyDateCreate,   
           
        getCardPage, fileUploader,
        handleDownloadFiles, handleDeleteFiles,
        handleBtnSave, handleBtnClose, 
    }

    /** Свойства передеваемые во вкладку primary */
    const propsToTabPrimary: ICardCompanyTabPrimary = {
        fieldsError, commpanyINNAuto, 
        isAddressDisabled: !isOpen || isLoadingCard,
        txtINN, txtKPP, isReadOnlyKPP, 
        txtCompanyPhone, txtCompanyEmail,
        txtCompanyName, txtCompanyNameShort, txtManagerPosition, txtActingOnBasis,
        txtLastName, txtFirstName, txtSecondName, txtInPersonWhom, txtAddress, txtRequisites, txtComment,
        setTxtManagerPosition, setTxtLastName, setTxtFirstName, setTxtSecondName,
        handleAutoINN, handleChangeINN, handleChangeKPP,
    }

    /** Свойства передеваемые во вкладку файлы */
    const propsToTabFiles: ICardCompanyTabFiles = {
        filesList, handleSelectedFiles, handleSelectedFilesAll, fileUploader, handleDownloadFiles, handleDeleteFiles, handleOpenModalPreview,
    }

    return <>
        <CardCompany {...propsToUI} />

        <ModalConfirm {...modalConfirm.value} />
        <ModalNotice {...modalNotice.value} />
        <ModalPreview {...modalPreview.value} />
    </>
}